import {useEffect} from "react";
import {Alert} from "@/primitives/Alert";
import {AlertDialog} from "@/primitives/AlertDialog";
import {Button} from "@/primitives/Button";
import {TerminalQrCodeCard, type TerminalQrCodeUser} from "@/features/user/terminalQrCodeCard";
import {ReactIsland, type WithCommandListener} from "@/shared/reactIsland";
import {useDialogState} from "@/shared/useDialogState";

export interface AddUserSuccessDetails {
	user: TerminalQrCodeUser;
	/** The user was created with a terminal QR code that was not mailed, so it is shown here. */
	showQrCode: boolean;
	showTimeAccountInit: boolean;
	/** Target of the initailize time account button; only read when {@link showTimeAccountInit} is set. */
	initializeTimeAccountLink: string;
}

export type AddUserSuccessModalIslandCommands = {
	show: AddUserSuccessDetails;
};

export function AddUserSuccessModalIsland({
											  addCommandListener,
										  }: WithCommandListener<AddUserSuccessModalIslandCommands>) {
	const dialog = useDialogState<AddUserSuccessDetails>();

	const {show} = dialog;
	useEffect(() => addCommandListener("show", show), [addCommandListener, show]);

	/* Root stays mounted for the fade transitions; `value` outlives the close so the texts survive the fade out */
	return (
		<AlertDialog.Root open={dialog.open} onOpenChange={dialog.onOpenChange}>
			{dialog.value != null && <AddUserSuccessContent details={dialog.value}/>}
		</AlertDialog.Root>
	);
}

function AddUserSuccessContent({details}: {details: AddUserSuccessDetails}) {
	const {user, showQrCode, showTimeAccountInit, initializeTimeAccountLink} = details;

	return (
		<AlertDialog.Content variation="primary" size="lg" initialFocus={false}>
			<AlertDialog.Header>
				<AlertDialog.Title>
					{messages.withArguments("user.addUserDialog.success", user.name)}
				</AlertDialog.Title>
			</AlertDialog.Header>
			<AlertDialog.Body>
				{showQrCode && (
					<>
						<TerminalQrCodeCard user={user} allowDelete={false}/>
						<Alert iconPosition="left" className="mt-15">
							{messages["user.addUserDialog.qrcode.info"]}
						</Alert>
					</>
				)}
				{showTimeAccountInit && (
					<p className={showQrCode ? "mt-20" : undefined}>
						{messages["user.addUserDialog.timeAccountInit"]}
					</p>
				)}
			</AlertDialog.Body>
			<AlertDialog.Footer>
				{showTimeAccountInit && (
					<Button
						variant="secondary"
						onClick={() => (window.location.href = initializeTimeAccountLink)}
					>
						{messages["user.addUserDialog.timeAccountInit.button"]}
					</Button>
				)}
				<AlertDialog.Close render={<Button/>}>
					{messages["user.addUserDialog.finish"]}
				</AlertDialog.Close>
			</AlertDialog.Footer>
		</AlertDialog.Content>
	);
}

/**
 * Mounts the island on its own container appended to the body
 */
export function mountAddUserSuccessModalIsland() {
	return new ReactIsland<object, AddUserSuccessModalIslandCommands>(
		AddUserSuccessModalIsland,
		{},
	);
}
